import logger from '~/services/logger';
import { getZappClientUUID } from '~/utils/analytics';
import {
  Mapper,
  type MapperConfiguration,
  type AnalyticsEvent,
} from './event-mapper';
import {
  PIANO_EVENTS,
  PIANO_DEVICE_PROPERTIES,
  PIANO_DEVICE_OS,
  PIANO_DEVICE_TYPES,
  PIANO_PLATFORMS,
  PIANO_USER_PROPERTIES,
  PIANO_EVENT_PROPERTIES,
  PIANO_HTTP_CONFIG,
  PIANO_COLLECTION_PLATFORM,
  PIANO_STORAGE_KEYS,
  PIANO_USER_AGENT_PATTERNS,
  PIANO_DEVICE_MANUFACTURERS,
} from './piano-constants';

export interface PianoAnalyticsConfig {
  collectionDomain: string;
  siteId: string;
  enabled: boolean;
}

export interface PianoEvent {
  name: string;
  data: Record<string, any>;
}

export interface PianoAnalyticsData {
  eventName: string;
  trackData: Record<string, any>;
}

class PianoAnalyticsService {
  private config: PianoAnalyticsConfig | null = null;
  private visitorId: string | null = null;
  private mapper: Mapper | null = null;

  initialize(config: PianoAnalyticsConfig) {
    this.config = config;
    this.visitorId = getZappClientUUID();

    if (config.enabled) {
      logger.info(
        `Piano Analytics initialized - Domain: ${config.collectionDomain}, Site: ${config.siteId}`
      );
    }
  }

  setMapperConfiguration(mapperConfig: MapperConfiguration) {
    this.mapper = new Mapper();
    this.mapper.setConfiguration(mapperConfig);
    logger.info('Piano Analytics mapper initialized with custom configuration');
  }

  private async sendEvents(events: PianoEvent[]): Promise<void> {
    if (!this.config?.enabled || !this.visitorId) {
      return;
    }

    try {
      const endpoint = `https://${this.config.collectionDomain}/event?s=${this.config.siteId}&idclient=${this.visitorId}`;

      const response = await fetch(endpoint, {
        method: PIANO_HTTP_CONFIG.METHOD,
        headers: {
          'Content-Type': PIANO_HTTP_CONFIG.CONTENT_TYPE,
        },
        body: JSON.stringify({ events }),
      });

      if (!response.ok) {
        throw new Error(`Piano Analytics request failed: ${response.status}`);
      }

      logger.info('Piano Analytics event sent successfully');
    } catch (error) {
      logger.error(`Failed to send Piano Analytics event: ${error}`);
    }
  }

  private getDeviceProperties(): Record<string, any> {
    return {
      [PIANO_DEVICE_PROPERTIES.DEVICE_TIMESTAMP_UTC]: Math.floor(
        Date.now() / 1000
      ),
      [PIANO_DEVICE_PROPERTIES.EVENT_COLLECTION_PLATFORM]:
        PIANO_COLLECTION_PLATFORM.API,
      [PIANO_DEVICE_PROPERTIES.EVENT_COLLECTION_VERSION]:
        PIANO_COLLECTION_PLATFORM.VERSION,
      [PIANO_DEVICE_PROPERTIES.DEVICE_DISPLAY_HEIGHT]: window.innerHeight,
      [PIANO_DEVICE_PROPERTIES.DEVICE_DISPLAY_WIDTH]: window.innerWidth,
      [PIANO_DEVICE_PROPERTIES.DEVICE_SCREEN_HEIGHT]: window.screen.height,
      [PIANO_DEVICE_PROPERTIES.DEVICE_SCREEN_WIDTH]: window.screen.width,
      [PIANO_DEVICE_PROPERTIES.DEVICE_OS]: this.getDeviceOS(),
      [PIANO_DEVICE_PROPERTIES.DEVICE_OS_VERSION]: this.getDeviceOSVersion(),
      [PIANO_DEVICE_PROPERTIES.DEVICE_TYPE]: this.getDeviceType(),
      [PIANO_DEVICE_PROPERTIES.DEVICE_MANUFACTURER]:
        this.getDeviceManufacturer(),
      [PIANO_DEVICE_PROPERTIES.PLATFORM]: this.getPlatform(),
      [PIANO_DEVICE_PROPERTIES.DOMAIN]: window.location.hostname,
      [PIANO_DEVICE_PROPERTIES.PREVIOUS_URL]: document.referrer || '',
    };
  }

  private getDeviceOS(): string {
    if (typeof navigator === 'undefined') {
      return PIANO_DEVICE_OS.UNKNOWN;
    }

    const userAgent = navigator.userAgent;
    if (PIANO_USER_AGENT_PATTERNS.IOS.test(userAgent))
      return PIANO_DEVICE_OS.IOS;
    if (PIANO_USER_AGENT_PATTERNS.ANDROID.test(userAgent))
      return PIANO_DEVICE_OS.ANDROID;
    if (PIANO_USER_AGENT_PATTERNS.WINDOWS.test(userAgent))
      return PIANO_DEVICE_OS.WINDOWS;
    if (PIANO_USER_AGENT_PATTERNS.MAC.test(userAgent))
      return PIANO_DEVICE_OS.MACOS;
    if (PIANO_USER_AGENT_PATTERNS.LINUX.test(userAgent))
      return PIANO_DEVICE_OS.LINUX;
    return PIANO_DEVICE_OS.UNKNOWN;
  }

  private getDeviceOSVersion(): number {
    const userAgent = navigator.userAgent;
    const match = userAgent.match(PIANO_USER_AGENT_PATTERNS.OS_VERSION);
    if (match) {
      return parseFloat(`${match[1]}.${match[2]}`);
    }
    return 0;
  }

  private getDeviceType(): string {
    const userAgent = navigator.userAgent;
    if (PIANO_USER_AGENT_PATTERNS.MOBILE.test(userAgent))
      return PIANO_DEVICE_TYPES.MOBILE;
    if (PIANO_USER_AGENT_PATTERNS.TABLET.test(userAgent))
      return PIANO_DEVICE_TYPES.TABLET;
    return PIANO_DEVICE_TYPES.DESKTOP;
  }

  private getDeviceManufacturer(): string {
    const userAgent = navigator.userAgent;
    if (PIANO_USER_AGENT_PATTERNS.IOS.test(userAgent))
      return PIANO_DEVICE_MANUFACTURERS.IPHONE;
    if (PIANO_USER_AGENT_PATTERNS.ANDROID.test(userAgent))
      return PIANO_DEVICE_MANUFACTURERS.ANDROID;
    return PIANO_DEVICE_MANUFACTURERS.UNKNOWN;
  }

  private getPlatform(): string {
    const userAgent = navigator.userAgent;
    if (PIANO_USER_AGENT_PATTERNS.IOS.test(userAgent))
      return PIANO_PLATFORMS.IOS;
    if (PIANO_USER_AGENT_PATTERNS.ANDROID.test(userAgent))
      return PIANO_PLATFORMS.ANDROID;
    if (PIANO_USER_AGENT_PATTERNS.WINDOWS.test(userAgent))
      return PIANO_PLATFORMS.WINDOWS;
    if (PIANO_USER_AGENT_PATTERNS.MAC.test(userAgent))
      return PIANO_PLATFORMS.MACOS;
    if (PIANO_USER_AGENT_PATTERNS.LINUX.test(userAgent))
      return PIANO_PLATFORMS.LINUX;
    return PIANO_PLATFORMS.WEB;
  }

  private getUserProperties(): Record<string, any> {
    return {
      [PIANO_USER_PROPERTIES.USER]: this.visitorId,
      [PIANO_USER_PROPERTIES.SESSION]: this.getSessionId(),
      [PIANO_USER_PROPERTIES.ACCOUNT]: this.config?.siteId,
    };
  }

  private getSessionId(): string {
    try {
      const sessionId =
        localStorage.getItem(PIANO_STORAGE_KEYS.SESSION_ID) || '';
      return sessionId;
    } catch (error: any) {
      logger.error(
        `Error accessing localStorage for Piano session ID: ${error.message}`
      );
      return '';
    }
  }

  async trackEvent(
    eventName: string,
    properties: Record<string, any> = {}
  ): Promise<void> {
    if (!this.config?.enabled || typeof window === 'undefined') {
      return;
    }

    // Add contextual properties
    const eventData = {
      ...properties,
      ...this.getDeviceProperties(),
      ...this.getUserProperties(),
      [PIANO_EVENT_PROPERTIES.EVENT]: eventName,
      [PIANO_EVENT_PROPERTIES.TIME]: Date.now().toString(),
    };

    const event: PianoEvent = {
      name: eventName,
      data: eventData,
    };

    await this.sendEvents([event]);
  }

  async trackPlayerEvent(
    eventName: string,
    playerData: Record<string, any> = {}
  ): Promise<void> {
    if (!this.config?.enabled || typeof window === 'undefined') {
      return;
    }

    // Add Piano Analytics properties first
    let enrichedPlayerData: Record<string, any> = {
      ...playerData,
      ...this.getDeviceProperties(),
      ...this.getUserProperties(),
      [PIANO_EVENT_PROPERTIES.EVENT]: eventName,
      [PIANO_EVENT_PROPERTIES.TIME]: Date.now().toString(),
    };
    // Apply mapper transformation if available
    if (this.mapper) {
      try {
        const analyticsEvent: AnalyticsEvent = {
          name: eventName,
          params: enrichedPlayerData,
        };

        const mappedEvent = await this.mapper.map(analyticsEvent);

        if (mappedEvent) {
          eventName = mappedEvent.name;
          enrichedPlayerData = mappedEvent.params;
          logger.info(`Event mapped: ${eventName} → ${mappedEvent.name}`);
        } else {
          logger.info(`Event ${eventName} was filtered out by mapper`);
          return; // Don't send filtered events
        }
      } catch (error) {
        logger.info(
          `Error mapping event ${eventName}, sending unmapped: ${error}`
        );
      }
    }

    const event: PianoEvent = {
      name: eventName,
      data: enrichedPlayerData,
    };

    await this.sendEvents([event]);
  }

  async trackPageView(pageData: Record<string, any> = {}): Promise<void> {
    await this.trackEvent(PIANO_EVENTS.PAGE_DISPLAY, {
      ...pageData,
      [PIANO_EVENT_PROPERTIES.PAGE_URL]: window.location.href,
      [PIANO_EVENT_PROPERTIES.PAGE_TITLE]: document.title,
    });
  }

  async trackPurchase(purchaseData: Record<string, any> = {}): Promise<void> {
    await this.trackEvent(PIANO_EVENTS.PURCHASE, purchaseData);
  }

  async trackUser(
    userId: string,
    userData: Record<string, any> = {}
  ): Promise<void> {
    await this.trackEvent(PIANO_EVENTS.USER_IDENTIFICATION, {
      [PIANO_EVENT_PROPERTIES.USER_ID]: userId,
      ...userData,
    });
  }
}

// Singleton instance
export const pianoAnalytics = new PianoAnalyticsService();

// Export individual functions for easier usage
export const trackPianoEvent = (
  eventName: string,
  properties: Record<string, any> = {}
) => pianoAnalytics.trackEvent(eventName, properties);

export const trackPianoPlayerEvent = (
  eventName: string,
  playerData: Record<string, any> = {}
) => pianoAnalytics.trackPlayerEvent(eventName, playerData);

export const trackPianoPageView = (pageData: Record<string, any> = {}) =>
  pianoAnalytics.trackPageView(pageData);

export const trackPianoPurchase = (purchaseData: Record<string, any> = {}) =>
  pianoAnalytics.trackPurchase(purchaseData);

export const trackPianoUser = (
  userId: string,
  userData: Record<string, any> = {}
) => pianoAnalytics.trackUser(userId, userData);

export const setPianoAnalyticsMapperConfiguration = (
  mapperConfig: MapperConfiguration
) => pianoAnalytics.setMapperConfiguration(mapperConfig);
